/**
 * Environment switcher (ADR-0008, Phase C) — the composer-footer control for
 * moving a conversation between execution environments, styled like ModelSelector.
 *
 * Shows where the active conversation currently runs (Cloud / a device) and, on
 * open, lists the environments it can move to: Cloud is always available; a device
 * is offered only while it's online (fresh heartbeat). Picking a target calls the
 * transfer endpoint (which flips the single-writer lease + syncs state); the
 * resulting `conversation_upsert` broadcast updates the badge everywhere. When a
 * turn was refused here because the session runs elsewhere (`executionElsewhere`),
 * the trigger highlights with a "runs on X — move it" affordance.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { listEnvironments, transferConversation, type Environment } from "@/api/environment";
import { useChatStore, useConversationIsRunning } from "@/stores/chatStore";

function CloudIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 15a4 4 0 004 4h11a3 3 0 00.5-5.96 5 5 0 00-9.78-1.63A3.5 3.5 0 003 15z" />
    </svg>
  );
}

function DeviceIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a1 1 0 011-1h14a1 1 0 011 1v9H4V6zM2 19h20l-1.5-2H3.5L2 19z" />
    </svg>
  );
}

/** Friendly label for an execution_location string, resolving device ids to names. */
function labelFor(loc: string | null | undefined, envs: Environment[]): string {
  if (!loc || loc === "cloud") return "Cloud";
  const match = envs.find((e) => e.id === loc);
  return match?.label ?? "This device";
}

export function EnvironmentSelector() {
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const conversations = useChatStore((s) => s.conversations);
  const executionElsewhere = useChatStore((s) => s.executionElsewhere);
  const setExecutionElsewhere = useChatStore.setState;
  // Can't move a conversation mid-turn: the move flips the assignment and
  // resumes from the synced session — it does NOT migrate the running process,
  // so the turn and anything it spawned (background sub-agents especially) die
  // on the source machine. The server refuses this outright; disabling here is
  // what makes the refusal legible instead of an error after the click.
  //
  // Reads the DURABLE row, not just the local task set: a turn running on the
  // other environment never enters ``runningTasks`` here, and that is precisely
  // the case where moving costs the most.
  const isRunning = useConversationIsRunning(activeConversationId);

  const [open, setOpen] = useState(false);
  const [envs, setEnvs] = useState<Environment[]>([]);
  // This backend's OWN environment id — the default for an unassigned conversation
  // (execution_location NULL), so a legacy chat shows where it'd actually run
  // (this device on the desktop) rather than always "Cloud".
  const [backendCurrent, setBackendCurrent] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // target id being transferred
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const activeConv = conversations.find((c) => c.id === activeConversationId);
  const currentLoc = activeConv?.execution_location ?? backendCurrent ?? "cloud";
  const runsElsewhere =
    executionElsewhere?.conversationId === activeConversationId ? executionElsewhere : null;

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Load environments + this backend's own env. Runs on mount (so the trigger
  // shows the right default immediately) and again when the menu opens (fresh
  // device availability).
  const load = useCallback(() => {
    let cancelled = false;
    listEnvironments()
      .then((res) => {
        if (cancelled) return;
        setEnvs(res.environments);
        setBackendCurrent(res.current);
        setError(null);
      })
      .catch(() => !cancelled && setError("Couldn't load environments"));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);
  useEffect(() => {
    if (open) return load();
  }, [open, load]);
  // A turn starting while the menu is open closes it — no switching mid-run.
  useEffect(() => {
    if (isRunning) setOpen(false);
  }, [isRunning]);

  if (!activeConversationId) return null;

  const move = async (target: Environment) => {
    if (target.id === currentLoc || busy || isRunning) return;
    setBusy(target.id);
    setError(null);
    try {
      await transferConversation(activeConversationId, target.id);
      // The conversation_upsert broadcast updates execution_location; clear any
      // "runs elsewhere" prompt for this conversation.
      setExecutionElsewhere((s) => ({
        executionElsewhere:
          s.executionElsewhere?.conversationId === activeConversationId ? null : s.executionElsewhere,
      }));
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Move failed");
    } finally {
      setBusy(null);
    }
  };

  const triggerLabel = labelFor(currentLoc, envs);
  const isCloud = !currentLoc || currentLoc === "cloud";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={isRunning}
        title={
          isRunning
            ? "Can't switch environment while a turn is running"
            : runsElsewhere
              ? `Runs on ${labelFor(runsElsewhere.location, envs)} — move it`
              : "Execution environment"
        }
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-colors
          ${isRunning
            ? "bg-space-20 text-space-50 opacity-60 cursor-default"
            : runsElsewhere
              ? "bg-hydra/10 text-hydra ring-1 ring-hydra/30 hover:bg-hydra/15"
              : "bg-space-20 text-space-50 hover:bg-space-30 hover:text-space"}`}
      >
        {isCloud ? <CloudIcon className="h-3.5 w-3.5 shrink-0" /> : <DeviceIcon className="h-3.5 w-3.5 shrink-0" />}
        <span>{runsElsewhere ? `Runs on ${labelFor(runsElsewhere.location, envs)}` : triggerLabel}</span>
        <svg
          className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          className="fixed inset-x-3 bottom-24 max-h-[70vh] overflow-auto z-50
                     sm:absolute sm:bottom-full sm:inset-x-auto sm:left-0 sm:mb-2 sm:w-64
                     bg-surface border border-space-30 rounded-xl shadow-lg"
        >
          <div className="px-3 py-2 border-b border-space-20">
            <p className="text-[11px] font-semibold text-space">Run this conversation in</p>
            <p className="text-[11px] leading-snug text-space-50">
              Move it to the cloud to keep it running after you close this device.
            </p>
          </div>
          {envs.length === 0 && !error && (
            <p className="px-3 py-3 text-[11px] text-space-50">Loading…</p>
          )}
          {envs.map((env) => {
            const isCurrent = env.id === currentLoc || (isCloud && env.id === "cloud");
            const disabled = !env.available || isCurrent || busy !== null || isRunning;
            return (
              <button
                key={env.id}
                type="button"
                disabled={disabled}
                onClick={() => move(env)}
                className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5 transition-colors border-b border-space-20 last:border-b-0
                  ${disabled ? "opacity-60 cursor-default" : "hover:bg-space-20"}`}
              >
                {env.kind === "cloud" ? <CloudIcon className="h-4 w-4 shrink-0 text-space-50" /> : <DeviceIcon className="h-4 w-4 shrink-0 text-space-50" />}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-space truncate">{env.label}</p>
                  <p className="text-[10px] text-space-50">
                    {env.kind === "cloud" ? "Always on · sandboxed" : env.available ? "Online" : "Offline"}
                  </p>
                </div>
                {isCurrent ? (
                  <span className="text-[10px] font-medium text-hydra">Current</span>
                ) : env.available ? (
                  busy === env.id ? (
                    <span className="text-[10px] text-space-50">Moving…</span>
                  ) : (
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                  )
                ) : (
                  <span className="w-1.5 h-1.5 rounded-full bg-space-40 shrink-0" />
                )}
              </button>
            );
          })}
          {error && <p className="px-3 py-2 text-[11px] text-red-500">{error}</p>}
        </div>
      )}
    </div>
  );
}
